/**
 * Sistema de Animações Centralizado
 * Gerencia animações de surgimento e cascata baseadas em Intersection Observer
 */

(function() {
  'use strict';

  /**
   * Inicializa animação para um elemento específico
   * @param {string} selector - Seletor CSS do elemento
   * @param {Object} options - Opções de configuração
   * @param {Function} [options.onVisible] - Callback quando elemento fica visível
   * @param {string} [options.animatedClass] - Classe que indica que o elemento está animado
   */
  function initAnimation(selector, options = {}) {
    const element = document.querySelector(selector);
    const animatedClass = options.animatedClass || '--animated';
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!element || !element.className.includes(animatedClass)) {
      return;
    }

    if (prefersReducedMotion) {
      element.classList.add('is-visible');
      if (options.onVisible) options.onVisible(element);
      return;
    }

    function checkAndAnimate() {
      const rect = element.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      if (isVisible) {
        element.classList.add('is-visible');
        if (options.onVisible) options.onVisible(element);
        return true;
      }
      return false;
    }

    if (checkAndAnimate()) return;

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          if (options.onVisible) options.onVisible(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { root: null, rootMargin: '50px', threshold: 0.01 });

    observer.observe(element);
  }

  /**
   * Inicializa animação em cascata (múltiplos elementos em sequência)
   * @param {string} containerSelector - Seletor do container (para IntersectionObserver)
   * @param {string} itemSelector - Seletor dos elementos que animam (ex: .cascade-item)
   * @param {Object} options - cascadeType, cascadeDuration, cascadeInitialDelay, cascadeStagger
   */
  function initCascade(containerSelector, itemSelector, options = {}) {
    let container;
    if (typeof containerSelector === 'string' && containerSelector.charAt(0) === '#') {
      container = document.getElementById(containerSelector.slice(1));
    } else {
      container = document.querySelector(containerSelector);
    }
    if (!container) return;

    const items = container.querySelectorAll(itemSelector);
    if (!items.length) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      items.forEach(function(el) {
        el.style.opacity = '1';
      });
      return;
    }

    const type = options.cascadeType || 'fade-in';
    const duration = options.cascadeDuration || 600;
    const initialDelay = options.cascadeInitialDelay || 300;
    const stagger = options.cascadeStagger || 200;

    container.style.setProperty('--cascade-duration', duration + 'ms');

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        items.forEach(function(el, index) {
          el.classList.add('cascade-type-' + type);
          el.style.animationDelay = (initialDelay + index * stagger) + 'ms';
          setTimeout(function() {
            el.classList.add('cascade-visible');
          }, 10);
        });
        observer.unobserve(entry.target);
      });
    }, { root: null, rootMargin: '0px', threshold: 0.1 });

    observer.observe(container);
  }

  /**
   * Inicializa animação de divider
   */
  function initDividerAnimation(selector) {
    initAnimation(selector, { animatedClass: 'divider__line--animated' });
  }

  /**
   * Inicializa animação de typewriter
   */
  function initTypewriter(selector, options = {}) {
    const element = document.querySelector(selector);
    if (!element) return;
    const speed = options.speed || 30;
    const delay = options.delay || 0;
    const originalText = element.textContent || element.innerText;
    const originalHTML = element.innerHTML;
    element.innerHTML = '';
    element.style.opacity = '1';
    let currentIndex = 0;
    function addNextChar() {
      if (currentIndex >= originalText.length) {
        element.innerHTML = originalHTML;
        element.classList.add('typewriter-complete');
        return;
      }
      element.appendChild(document.createTextNode(originalText[currentIndex]));
      currentIndex++;
      setTimeout(addNextChar, speed);
    }
    setTimeout(addNextChar, delay);
  }

  /**
   * Inicializa múltiplas animações de typewriter em uma lista
   */
  function initTypewriterList(selector, options = {}) {
    const elements = document.querySelectorAll(selector);
    if (!elements.length) return;
    const speed = options.speed || 30;
    const delay = options.delay || 0;
    const staggerDelay = options.staggerDelay || 200;
    elements.forEach(function(element, index) {
      const originalText = element.getAttribute('data-original-text') || element.textContent || element.innerText;
      const originalHTML = element.getAttribute('data-original-html') || element.innerHTML;
      element.innerHTML = '';
      element.style.opacity = '1';
      let currentIndex = 0;
      function addNextChar() {
        if (currentIndex >= originalText.length) {
          element.innerHTML = originalHTML;
          element.classList.add('typewriter-complete');
          return;
        }
        element.appendChild(document.createTextNode(originalText[currentIndex]));
        currentIndex++;
        setTimeout(addNextChar, speed);
      }
      setTimeout(addNextChar, delay + (index * staggerDelay));
    });
  }

  window.AnimationSystem = {
    init: initAnimation,
    initCascade: initCascade,
    initDivider: initDividerAnimation,
    initTypewriter: initTypewriter,
    initTypewriterList: initTypewriterList
  };
})();
